import { useStore } from '@nanostores/react';
import { useCallback } from 'react';

import { DowngradeWarning } from './DowngradeWarning';
import { TokenReloadSettingsSection } from './TokenReload';
import { Button } from '~/components/ui/Button';
import { PricingPrice, PricingSettingSectionWrapper } from '~/components/ui/pricing/common';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import { teamPricingSettingsStore } from '~/lib/stores/pricing/settings/teamSettings';
import { teamPricingPlansStore } from '~/lib/stores/pricing/teamPlans';
import { userStore } from '~/lib/stores/user';
import { PRICING_URL } from '~/lib/urls';
import { AnalyticsAction, AnalyticsTrackEvent, PricingPageOpenedTrigger } from '~/types/analytics';
import type { OrganizationData } from '~/types/organization';
import type { SubscriptionInterval } from '~/types/subscription';

interface Props {
  organization: OrganizationData;
  closeSettingsDialog: () => void;
}

export const TeamPlanPricingSettingsSection = ({ organization, closeSettingsDialog }: Props) => {
  const nextPricing = useStore(teamPricingSettingsStore.nextHigherTierPricing);
  const isLoading = useStore(teamPricingSettingsStore.isLoading);
  const subscriptionInterval = organization?.billing?.subscription?.interval;

  const openPricingOverlay = useCallback(
    (action: 'see-all' | 'renew') => {
      closeSettingsDialog();

      if (window.location.pathname !== PRICING_URL) {
        pricingStore.openPricingOverlay(PricingPageOpenedTrigger.SettingsTokenTab);
      }

      if (action === 'renew') {
        teamPricingPlansStore.selectActivePricing();
      }
    },
    [closeSettingsDialog],
  );

  const onSeeAllPlansClick = useCallback(() => {
    openPricingOverlay('see-all');
  }, [openPricingOverlay]);

  const onRenewClick = useCallback(() => {
    openPricingOverlay('renew');
  }, [openPricingOverlay]);

  const onUpgradeToNextTier = useCallback(() => {
    const user = userStore.getUser();

    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.TeamPlanUpgradeClicked,
        properties: {
          // user context (who triggered the action)
          user_email: user?.email,
          user_role: organization.role,
          user_id: user?.id,

          // organization context
          organization_id: organization.id,
          organization_slug: organization.slug,

          // plan context
          current_tier: organization?.billing?.tier,
          target_tier: nextPricing?.tier,
          billing_interval: subscriptionInterval || 'monthly',
          member_count: organization?.billing?.subscription?.quantity || 1,

          // action context
          location: 'team_settings',
        },
      },
    });

    teamPricingSettingsStore.upgradeToNextTier();
  }, []);

  if (!nextPricing) {
    if (organization.role === 'admin') {
      return <TokenReloadSettingsSection organization={organization} />;
    }

    return <TeamMemberReloadSection />;
  }

  if (organization.role !== 'admin') {
    return <TeamMemberSection />;
  }

  const quantity = organization?.billing?.subscription?.quantity || 1;
  const interval: SubscriptionInterval = organization?.billing?.subscription?.interval || 'monthly';
  const isCanceledSubscription = organization?.billing?.subscription?.status === 'canceled';

  const totalMonthlyPrice =
    interval === 'monthly' ? nextPricing.rateMonthly.month * quantity : nextPricing.rateMonthly.year * quantity;

  return (
    <>
      <PricingSettingSectionWrapper className="flex flex-col sm:grid sm:grid-cols-5 gap-2 sm:gap-6">
        <div className="flex flex-col items-start gap-2 sm:col-start-1 sm:col-end-4">
          <span className="font-semibold text-lg">Get more out of Teams</span>

          <span className="text-bolt-ds-textSecondary text-sm">
            Switch to the next tier and receive{' '}
            <span className="text-bolt-ds-textPrimary font-medium">
              {nextPricing.tokens} tokens per member per month.
            </span>
          </span>

          <Button variant="link-color" onClick={onSeeAllPlansClick}>
            See all available plans
          </Button>
        </div>

        <div className="flex flex-col gap-3 items-start sm:items-end justify-between mt-4 sm:mt-0 sm:col-start-4 sm:col-end-6">
          <PricingPrice
            label="per month"
            interval={interval}
            pricing={nextPricing}
            size="sm"
            price={totalMonthlyPrice}
          />

          <Button onClick={onUpgradeToNextTier} loading={isLoading} disabled={isCanceledSubscription}>
            Upgrade plan
          </Button>
        </div>

        {isCanceledSubscription && <DowngradeWarning type="team" onRenewClick={onRenewClick} />}
      </PricingSettingSectionWrapper>

      {subscriptionInterval === 'yearly' && <TokenReloadSettingsSection organization={organization} needCompact />}
    </>
  );
};

const TeamMemberSection = () => {
  return (
    <PricingSettingSectionWrapper className="flex flex-col gap-2">
      <span className="text-lg font-semibold">Need more tokens?</span>

      <span className="text-sm text-bolt-ds-textSecondary">
        Ask your admin for a plan upgrade or monthly token refill.
      </span>
    </PricingSettingSectionWrapper>
  );
};

const TeamMemberReloadSection = () => {
  return (
    <PricingSettingSectionWrapper className="flex flex-col gap-2">
      <span className="text-lg font-semibold">Running out of tokens?</span>

      <span className="text-sm text-bolt-ds-textSecondary">
        Ask your admin for a token reload. It resets your monthly tokens and you can get back to prompting immediately.
      </span>
    </PricingSettingSectionWrapper>
  );
};
